import Pagination from "../../components/Pagination"; 
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Box, TableContainer, Paper,
  TextField, Button, Modal, Typography, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, Grid, Card, CardMedia, CardContent
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../../components/Table/Tables.css";
import { API_BASE } from '../../utils/api';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";

function TableSub({ searchTerm }) {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  const [touchStartY, setTouchStartY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setTouchStartY(e.touches[0].clientY);
      setIsScrolling(false);
    }
  };
  
  const handleTouchMove = (e) => {
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
    if (deltaY > 10) setIsScrolling(true);
  };

  const handleTouchEnd = (e, subCat) => {
    if (e.changedTouches.length === 1 && !isScrolling) {
      handleViewClick(subCat, e);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const subCatResponse = await axios.get(`${API_BASE}/api/subcategory/display`);
        setSubCategories(subCatResponse.data);
        const catResponse = await axios.get(`${API_BASE}/api/categories/all`);
        setCategories(catResponse.data);
      } catch (err) {
        setError("Failed to load categories or subcategories");
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const filteredSubCategories = subCategories.filter((subCat) => {
    const categoryName = subCat.category?.name?.toLowerCase() || "";
    const subCatName = subCat.subCat?.toLowerCase() || "";
    const lowerSearchTerm = searchTerm.toLowerCase();
    return categoryName.includes(lowerSearchTerm) || subCatName.includes(lowerSearchTerm);
  });

  const totalPages = Math.ceil(filteredSubCategories.length / itemsPerPage);
  const paginatedSubCategories = filteredSubCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const capitalize = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handleViewClick = (subCat, e) => {
    if (e) e.stopPropagation(); // prevent accidental click on modal buttons
    setCurrentSubCategory({ ...subCat });

    // Delay opening modal to avoid accidental Submit clicks
    setTimeout(() => {
      setOpenModal(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleFieldChange = (field, value) => {
    setCurrentSubCategory((prev) => {
      if (field === "category") {
        const selectedCategory = categories.find((cat) => cat.name === value);
        return { ...prev, category: selectedCategory };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleImageChange = (e) => {
    setCurrentSubCategory((prev) => ({
      ...prev,
      newImage: e.target.files[0],
      newImagePreview: URL.createObjectURL(e.target.files[0]),
    }));
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("subCat", currentSubCategory.subCat);
    formData.append("category", currentSubCategory.category._id);
    formData.append("isVisible", currentSubCategory.isVisible); 

    if (currentSubCategory.newImage) {
      formData.append("image", currentSubCategory.newImage);
    } else if (currentSubCategory.image) {
      formData.append("image", currentSubCategory.image);
    }

    try {
      const response = await axios.put(
        `${API_BASE}/api/subcategory/${currentSubCategory._id}`,
        formData
      );
      setSubCategories((prev) =>
        prev.map((subCat) =>
          subCat._id === currentSubCategory._id ? response.data : subCat
        )
      );
      toast.success("Subcategory updated successfully!");
      setOpenModal(false);
    } catch (err) {
      console.error("Error updating subcategory:", err);
      toast.error("Failed to update subcategory.");
    }
  };

  return (
    <Box className="main-container">
      {error && <p style={{ color: "red" }}>{error}</p>}
      
      <Grid container spacing={1}>
        {paginatedSubCategories.length > 0 ? (
          paginatedSubCategories.map((subCat) => (
            <Grid
              item
              xs={6} sm={4} md={4} lg={2}
              key={subCat._id}
              onDoubleClick={(e) => handleViewClick(subCat, e)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={(e) => handleTouchEnd(e, subCat)}
            >
              <Card className="category-card">
                {subCat.image ? (
                  <CardMedia
                    component="img"
                    className="category-image"
                    image={`${API_BASE}${subCat.image}`}
                    alt={subCat.subCat || "Subcategory Image"}
                  />
                ) : (
                  <Typography variant="body2" className="no-image">No Image</Typography>
                )}
                <CardContent>
                  <Typography variant="h6" className="category-name">
                    {capitalize(subCat.subCat)}
                  </Typography>
                  <Typography variant="body2" className="Subcategory-name">
                    {capitalize(subCat.category?.name)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : searchTerm.trim() !== "" ? (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", color: "black", width: "100%", fontFamily: "Roboto" }}
          >
            No results found
          </Typography>
        ) : null}
      </Grid>

      <Box className="pagination-container">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box className="modal-box">
          <CloseIcon onClick={() => setOpenModal(false)} className="close-icon"/>

          {currentSubCategory && (
            <>
              <Typography variant="h6" gutterBottom>Edit Subcategory</Typography>
              <TextField
                label="Subcategory Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={capitalize(currentSubCategory.subCat)}
                onChange={(e) => handleFieldChange("subCat", e.target.value)}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  value={currentSubCategory.category?.name || ""}
                  onChange={(e) => handleFieldChange("category", e.target.value)}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category.name}>
                      {capitalize(category.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {currentSubCategory.image && (
                <Box sx={{ mt: 2 }}>
                  <Typography>Current Image:</Typography>
                  <img
                    src={`${API_BASE}${currentSubCategory.image}`}
                    alt="Subcategory"
                    className="table-image"
                  />
                </Box>
              )}

              {currentSubCategory.newImagePreview && (
                <Box sx={{ mt: 2 }}>
                  <Typography>New Image:</Typography>
                  <img
                    src={currentSubCategory.newImagePreview}
                    alt="Preview"
                    className="table-image"
                  />
                </Box>
              )}

              <input type="file" onChange={handleImageChange} />
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currentSubCategory.isVisible || false}
                      onChange={(e) => handleFieldChange("isVisible", e.target.checked)}
                    />
                  }
                  label="Available"
                />
              </Box>

              <Box className="button-container">
                <Button variant="contained" onClick={handleSubmit}>Submit</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <ToastContainer position="top-center" autoClose={3000} />
    </Box>
  );
}

export default TableSub;
